import React, { ElementType, PropsWithChildren, useCallback, useMemo, useState } from 'react';
import type { EntityName } from '../../common/auth/Security';
import { SIDE_PANEL_IDS } from '../../common/constants/constants';
import { H } from '../../common/Layout';
import type { DataListCellActionHandlers } from '../../common/list/DataList/types';
import { getSelectedRowHighlightColor } from '../../common/list/selectedRowHighlight';
import { useStyling } from '../../common/Theme';
import DataListCellActions from './DataListCellActions';
import { useDataListCell } from './DataListCellContext';

export type DataListCellContainerProps = PropsWithChildren<{
    item: Record<string, any>;
    feature: string;
    /**
     * Optional only to support callers that still rely on the legacy permission fallback.
     * Make required once those callers forward it and the missing-entity fallback is removed.
     */
    entity?: EntityName;
    enableSelectItem?: boolean;
    selectedItemId?: string | null;
    updatedSelectedItemId?: (id: string | null) => void;
    onNotesPanelToggle?: (isOpen: boolean) => void;
    actionHandlers?: DataListCellActionHandlers;
    Container?: ElementType;
}>;

export default function DataListCellContainer({
    children,
    item,
    feature,
    entity,
    enableSelectItem = false,
    selectedItemId = null,
    updatedSelectedItemId,
    onNotesPanelToggle,
    actionHandlers = {},
    Container = H
}: DataListCellContainerProps) {
    const { isDarkMode } = useStyling();
    const [isHoverState, setIsHoverState] = useState<boolean>(false);
    const { isNotesPanelOpen, openPanelType } = useDataListCell();

    const isSelected = selectedItemId === item.id;

    const onContainerSelected = useCallback(() => {
        if (enableSelectItem && updatedSelectedItemId) {
            const wasSelected = isSelected;
            updatedSelectedItemId(isSelected ? null : item.id);

            if (!wasSelected && openPanelType) {
                switch (openPanelType) {
                    case SIDE_PANEL_IDS.notes:
                        if (actionHandlers.onNotes && isNotesPanelOpen) {
                            actionHandlers.onNotes(item);
                        }
                        break;
                    case SIDE_PANEL_IDS.attachments:
                        if (actionHandlers.onAttachments) {
                            actionHandlers.onAttachments(item);
                        }
                        break;
                    case SIDE_PANEL_IDS.attributes:
                        if (actionHandlers.onCustomAttribute) {
                            actionHandlers.onCustomAttribute(item);
                        }
                        break;
                }
            }
        }
    }, [enableSelectItem, updatedSelectedItemId, isSelected, actionHandlers, isNotesPanelOpen, item]);

    const enhancedActionHandlers = useMemo(() => {
        const handleNotesWithSelection = (item: Record<string, any>) => {
            // Update selection to the item whose notes are being opened
            if (enableSelectItem && updatedSelectedItemId && !isSelected) {
                updatedSelectedItemId(item.id);
            }

            // Open the panel and load notes
            onNotesPanelToggle?.(true);
            actionHandlers.onNotes?.(item);
        };

        const handleAttachmentsWithSelection = (item: Record<string, any>) => {
            // Update selection to the item whose attachments are being opened
            if (enableSelectItem && updatedSelectedItemId && !isSelected) {
                updatedSelectedItemId(item.id);
            }

            actionHandlers.onAttachments?.(item);
        };

        const handleCustomAttributeWithSelection = (item: Record<string, any>) => {
            // Update selection to the item whose custom attributes are being opened
            if (enableSelectItem && updatedSelectedItemId && !isSelected) {
                updatedSelectedItemId(item.id);
            }

            actionHandlers.onCustomAttribute?.(item);
        };

        return {
            ...actionHandlers,
            ...(actionHandlers.onNotes && { onNotes: handleNotesWithSelection }),
            ...(actionHandlers.onAttachments && { onAttachments: handleAttachmentsWithSelection }),
            ...(actionHandlers.onCustomAttribute && { onCustomAttribute: handleCustomAttributeWithSelection })
        };
    }, [actionHandlers, enableSelectItem, updatedSelectedItemId, isSelected, onNotesPanelToggle]);

    return (
        <Container
            sx={{
                position: 'relative',
                bgcolor: isSelected ? getSelectedRowHighlightColor(isDarkMode) : 'background.paper',
                borderRadius: 1,
                border: 1,
                borderColor: 'divider',
                overflow: 'hidden',
                gap: 1.5,
                px: 2,
                py: 1,
                cursor: enableSelectItem ? 'pointer' : 'default',
                overflowY: 'auto'
            }}
            onMouseEnter={() => setIsHoverState(true)}
            onMouseLeave={() => setIsHoverState(false)}
            onClick={onContainerSelected}
        >
            {children}
            <DataListCellActions
                item={item}
                feature={feature}
                entity={entity}
                actionHandlers={enhancedActionHandlers}
                isActivated={isHoverState || isSelected}
            />
        </Container>
    );
}
